import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface User {
  username: string
  role: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Check for existing session on app start
  useEffect(() => {
    const savedUser = localStorage.getItem('hospital_user')
    const savedToken = localStorage.getItem('hospital_token')
    
    if (savedUser && savedToken) {
      try {
        const parsedUser = JSON.parse(savedUser)
        // Verify token with backend
        verifyToken(savedToken, parsedUser)
      } catch (error) {
        localStorage.removeItem('hospital_user')
        localStorage.removeItem('hospital_token')
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [])

  const verifyToken = async (token: string, user: User) => {
    try {
      const apiUrl = import.meta.env.DEV 
        ? '/api/auth/verify' 
        : 'https://hospital-alert-worker.agustinosatinsky.workers.dev/auth/verify'
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })
      
      if (response.ok) {
        setUser(user)
      } else {
        // Token is invalid, clear storage
        localStorage.removeItem('hospital_user')
        localStorage.removeItem('hospital_token')
      }
    } catch (error) {
      console.error('Token verification error:', error)
      localStorage.removeItem('hospital_user')
      localStorage.removeItem('hospital_token')
    }
    setLoading(false)
  }

  const login = async (username: string, password: string): Promise<boolean> => {
    setLoading(true)
    
    try {
      const apiUrl = import.meta.env.DEV 
        ? '/api/auth/login' 
        : 'https://hospital-alert-worker.agustinosatinsky.workers.dev/auth/login'
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        const userData: User = {
          username: data.user.username,
          role: data.user.role
        }
        
        // Save to localStorage for persistence
        localStorage.setItem('hospital_user', JSON.stringify(userData))
        localStorage.setItem('hospital_token', data.token)
        
        setUser(userData)
        setLoading(false)
        return true
      } else {
        setLoading(false)
        return false
      }
    } catch (error) {
      console.error('Login error:', error)
      setLoading(false)
      return false
    }
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('hospital_user')
    localStorage.removeItem('hospital_token')
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}